import { useEffect, useMemo, useRef, useState } from 'react';
import { X, Plus } from 'lucide-react';
import { getPrixKgReference } from '../../utils/caisseProduct';

const DECOUPE_ORDER = ['chiffonade', 'fine', 'normale', 'epaisse'];

const DECOUPE_LABELS = {
  chiffonade: 'Chiffonade',
  fine: 'Fine',
  normale: 'Normale',
  epaisse: 'Épaisse',
};

function formatDecoupe(value) {
  if (!value) return '';
  const key = String(value).toLowerCase();
  return DECOUPE_LABELS[key] || value;
}

function parseNum(value) {
  return Number(String(value).replace(',', '.').trim()) || 0;
}

function calcTotalFromPrixKg(prixKg, grammes, quantite, unite = 'kg') {
  const poidsTotal = (Number(grammes) || 0) * Math.max(1, Number(quantite) || 1);
  if (!prixKg || poidsTotal <= 0) return 0;
  const u = String(unite).toLowerCase();
  if (u === '100g' || u === '100 g') {
    return (prixKg * poidsTotal) / 100;
  }
  return (prixKg * poidsTotal) / 1000;
}

/**
 * Saisie comptoir : poids et prix saisis manuellement par le caissier (balance + étiquette).
 */
export default function CaissePoidsModal({ produit, fmt, onClose, onConfirm }) {
  const portion = Number(produit?.poidsPortionStandard) || 100;
  const prixKgRef = useMemo(() => getPrixKgReference(produit), [produit]);
  const unite = produit?.unitePoids || 'kg';

  const presets = useMemo(
    () =>
      Array.from(new Set([50, 100, 150, 200, 250, 300, 500, portion].filter((p) => p > 0))).sort(
        (a, b) => a - b
      ),
    [portion]
  );

  const [poidsGrammes, setPoidsGrammes] = useState('');
  const [prixTotal, setPrixTotal] = useState('');
  const [typeDecoupe, setTypeDecoupe] = useState(produit?.decoupesDisponibles?.[0] || '');
  const [quantite, setQuantite] = useState(1);

  const poidsInputRef = useRef(null);
  const poidsBackupRef = useRef('');
  const prixBackupRef = useRef('');

  const decoupes = useMemo(() => {
    const raw = Array.isArray(produit?.decoupesDisponibles)
      ? produit.decoupesDisponibles.filter(Boolean)
      : [];
    return raw.slice().sort(
      (a, b) => (DECOUPE_ORDER.indexOf(a.toLowerCase()) >>> 0) - (DECOUPE_ORDER.indexOf(b.toLowerCase()) >>> 0)
    );
  }, [produit?.decoupesDisponibles]);

  const grammesNum = Math.max(0, parseNum(poidsGrammes));
  const prixTotalNum = Math.max(0, parseNum(prixTotal));

  const recalcPrix = (grams, qty = quantite) => {
    if (prixKgRef <= 0) return;
    const calc = calcTotalFromPrixKg(prixKgRef, grams, qty, unite);
    if (calc > 0) {
      const formatted = calc.toFixed(2);
      setPrixTotal(formatted);
      prixBackupRef.current = formatted;
    }
  };

  const appliquerPoids = (value, qty = quantite) => {
    setPoidsGrammes(String(value));
    poidsBackupRef.current = String(value);
    recalcPrix(parseNum(value), qty);
  };

  const changerQuantite = (delta) => {
    setQuantite((q) => {
      const next = Math.max(1, q + delta);
      if (grammesNum > 0) recalcPrix(grammesNum, next);
      return next;
    });
  };

  useEffect(() => {
    setPoidsGrammes('');
    setPrixTotal('');
    setQuantite(1);
    setTypeDecoupe(produit?.decoupesDisponibles?.[0] || '');
    poidsBackupRef.current = '';
    prixBackupRef.current = '';
    const t = setTimeout(() => poidsInputRef.current?.focus(), 80);
    return () => clearTimeout(t);
  }, [produit?.id]);

  const handleFocusClear = (currentValue, backupRef, setter) => (e) => {
    backupRef.current = currentValue;
    setter('');
    requestAnimationFrame(() => e.target.select());
  };

  const handleBlurRestore = (currentValue, backupRef, setter, onRestored) => () => {
    if (String(currentValue).trim() === '') {
      setter(backupRef.current);
      onRestored?.(backupRef.current);
    } else {
      backupRef.current = currentValue;
    }
  };

  const handlePoidsChange = (value) => {
    setPoidsGrammes(value);
    const g = parseNum(value);
    if (g > 0) recalcPrix(g);
  };

  const handlePoidsBlur = () => {
    if (String(poidsGrammes).trim() === '') {
      setPoidsGrammes(poidsBackupRef.current);
      recalcPrix(parseNum(poidsBackupRef.current));
      return;
    }
    poidsBackupRef.current = poidsGrammes;
    if (grammesNum > 0) recalcPrix(grammesNum);
  };

  const canSubmit = grammesNum > 0 && prixTotalNum > 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden max-h-[95vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 shrink-0">
          <div>
            <h2 className="text-lg font-bold text-slate-900">{produit?.nom}</h2>
            <p className="text-sm text-slate-500">Saisie comptoir — poids et prix manuels</p>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-full hover:bg-slate-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5 space-y-5 overflow-y-auto flex-1">
          <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-900">
            Saisissez le <strong>poids réel</strong> (balance) et le <strong>prix à encaisser</strong>.
            {prixKgRef > 0 ? (
              <>
                {' '}
                Référence : {prixKgRef.toFixed(2)} €/{unite} — le prix se calcule automatiquement
                quand vous saisissez le poids.
              </>
            ) : (
              <> Le prix au kg n’est pas défini en admin : saisissez le montant manuellement.</>
            )}
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-800 mb-2">
              Poids (grammes) *
            </label>
            <input
              ref={poidsInputRef}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={poidsGrammes}
              onChange={(e) => handlePoidsChange(e.target.value.replace(/[^\d.,]/g, ''))}
              onFocus={handleFocusClear(poidsGrammes, poidsBackupRef, setPoidsGrammes)}
              onBlur={handlePoidsBlur}
              className="w-full text-2xl font-bold text-center py-3 rounded-xl border-2 border-slate-200 focus:border-emerald-500 outline-none"
              placeholder="ex. 187"
            />
            <div className="flex flex-wrap gap-2 mt-2">
              {presets.map((g) => (
                <button
                  key={g}
                  type="button"
                  onClick={() => appliquerPoids(g)}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium border border-slate-200 hover:border-emerald-400 bg-slate-50"
                >
                  {g} g
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-800 mb-2">
              Prix total à encaisser *
            </label>
            <div className="relative">
              <input
                type="text"
                inputMode="decimal"
                value={prixTotal}
                onChange={(e) => {
                  const v = e.target.value.replace(/[^\d.,]/g, '');
                  setPrixTotal(v);
                }}
                onFocus={handleFocusClear(prixTotal, prixBackupRef, setPrixTotal)}
                onBlur={handleBlurRestore(prixTotal, prixBackupRef, setPrixTotal)}
                className="w-full text-2xl font-bold text-center py-3 pr-10 rounded-xl border-2 border-emerald-200 focus:border-emerald-500 outline-none"
                placeholder="0,00"
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 font-medium">
                €
              </span>
            </div>
            {prixKgRef > 0 && grammesNum > 0 && prixTotalNum > 0 && (
              <p className="text-xs text-emerald-700 mt-1 text-center">
                {grammesNum} g × {prixKgRef.toFixed(2)} €/{unite}
              </p>
            )}
          </div>

          {decoupes.length > 0 && (
            <div>
              <p className="text-sm font-medium text-slate-700 mb-2">Découpe (optionnel)</p>
              <div className="flex flex-wrap gap-2">
                {decoupes.map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setTypeDecoupe(d)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium border ${
                      typeDecoupe === d
                        ? 'bg-emerald-600 text-white border-emerald-600'
                        : 'bg-white text-slate-700 border-slate-200'
                    }`}
                  >
                    {formatDecoupe(d)}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-slate-700">Nombre de lignes</span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => changerQuantite(-1)}
                className="p-2 rounded-lg border border-slate-200"
              >
                −
              </button>
              <span className="w-8 text-center font-bold">{quantite}</span>
              <button
                type="button"
                onClick={() => changerQuantite(1)}
                className="p-2 rounded-lg border border-slate-200"
              >
                +
              </button>
            </div>
          </div>
        </div>

        <div className="p-4 border-t border-slate-100 bg-slate-50 shrink-0">
          <button
            type="button"
            disabled={!canSubmit}
            onClick={() =>
              onConfirm({
                poidsSouhaite: grammesNum,
                typeDecoupe: typeDecoupe || undefined,
                quantite,
                prixUnitaire: prixTotalNum / Math.max(1, quantite),
              })
            }
            className="w-full py-4 rounded-xl bg-emerald-600 text-white font-bold text-lg flex items-center justify-center gap-2 hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Plus className="h-6 w-6" />
            Ajouter — {canSubmit ? fmt(prixTotalNum) : '…'}
          </button>
        </div>
      </div>
    </div>
  );
}
